import { useContext } from 'react';
import { generatePath } from 'react-router';

import { gql } from '@apollo/client';

import { IntegrationType, useMicrosoftIntegrationQuery } from 'src/generated/mloop-graphql';

import { Routes } from 'src/constants/routes';

import { OnboardingContext } from 'src/contexts/OnboardingProvider';

import useCurrentRoute from 'src/hooks/useCurrentRoute';
import useEmployeeId from 'src/hooks/useEmployeeId';
import useOrgId from 'src/hooks/useOrgId';

import { DefaultReturnType } from './constants';
import { ReturnType } from './types';
import { getRedirectParams, isIgnoredRoutes } from './utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MicrosoftIntegration = gql`
  query MicrosoftIntegration {
    thisOrg {
      id
      integrations {
        id
        microsoft {
          active
        }
        microsoftGccHigh {
          active
        }
        microsoftCustom {
          active
        }
        microsoftServiceUser {
          serviceAccountEmail
        }
      }
    }
    thisEmployee {
      id
      integrations {
        id
        microsoft {
          active
        }
        microsoftGccHighEmployee {
          active
        }
        microsoftCustomEmployee {
          active
        }
        microsoftServiceUser {
          active
        }
      }
    }
  }
`;

/**
 * Hook to check if outlook user has granted access, if yes return redirect params to naviagte to outlook grant access screen
 * If needed to be export, lets discuss once
 */
const useOutlookGrantAccess = (): ReturnType => {
  /** Getting the current location */
  const currentRoute = useCurrentRoute();

  const orgId = useOrgId();
  const employeeId = useEmployeeId();

  const onboardingContext = useContext(OnboardingContext);

  const { data, loading, error } = useMicrosoftIntegrationQuery({
    skip: !orgId || !employeeId,
    context: {
      batch: false,
    },
  });

  const getNeededEmployeeIntegration = () => {
    if (loading || error) {
      return null;
    }

    const org = data?.thisOrg?.integrations;
    const emp = data?.thisEmployee?.integrations;

    if (org?.microsoft?.active && !emp?.microsoft?.active) {
      return IntegrationType.MicrosoftEmployee;
    }

    if (org?.microsoftGccHigh?.active && !emp?.microsoftGccHighEmployee?.active) {
      return IntegrationType.MicrosoftGccHighEmployee;
    }

    if (org?.microsoftCustom?.active && !emp?.microsoftCustomEmployee?.active) {
      return IntegrationType.MicrosoftCustomEmployee;
    }

    if (org?.microsoftServiceUser?.serviceAccountEmail && !emp?.microsoftServiceUser?.active) {
      return IntegrationType.MicrosoftServiceUserEmployee;
    }

    return null;
  };

  const neededEmployeeIntegration = getNeededEmployeeIntegration();
  if (
    loading ||
    error ||
    !neededEmployeeIntegration ||
    isIgnoredRoutes(currentRoute) ||
    onboardingContext.isDirectoryOAuthModalDismissed
  ) {
    return DefaultReturnType;
  }

  if (neededEmployeeIntegration) {
    const pathname = generatePath(Routes.GrantAccess, {
      integrationType: neededEmployeeIntegration,
    });
    return getRedirectParams(pathname, currentRoute);
  }

  return DefaultReturnType;
};

export default useOutlookGrantAccess;
